// Static lookup tables and pure helpers used by the pedagogy panel.
// Kept colocated to the organism since they encode display-side decisions
// (formula spelling, French tone labels, didactic tips per inversion)
// rather than core music-theory primitives.

export type FormulaPart = { degree: string; sup?: string };

export const INTERVAL_FORMULA: Record<number, FormulaPart> = {
  0: { degree: "1" },
  2: { degree: "2" },
  3: { degree: "3", sup: "m" },
  4: { degree: "3", sup: "M" },
  5: { degree: "4", sup: "J" },
  6: { degree: "5", sup: "°" },
  7: { degree: "5", sup: "J" },
  8: { degree: "5", sup: "+" },
  9: { degree: "6" },
  10: { degree: "7", sup: "m" },
  11: { degree: "7", sup: "M" },
};

export const TONE_LABEL: Record<number, string> = {
  0: "Tonique",
  2: "9e",
  3: "3ce mineure",
  4: "3ce majeure",
  5: "11e",
  6: "5te dim.",
  7: "5te juste",
  8: "5te aug.",
  9: "13e",
  10: "7e mineure",
  11: "7e majeure",
};

/**
 * Build a chord-tone degree label from an interval, e.g.:
 *   0  → "1"      (root)
 *   3  → "3m"     (third minor)
 *   4  → "3M"     (third major)
 *   6  → "5°"     (diminished fifth)
 *   7  → "5J"     (perfect fifth)
 *   8  → "5+"     (augmented fifth)
 *  10  → "7m"     (minor seventh)
 *  11  → "7M"     (major seventh)
 */
export function intervalDegree(interval: number): string {
  const f = INTERVAL_FORMULA[interval];
  if (!f) {
    return `+${interval}`;
  }

  return f.sup ? `${f.degree}${f.sup}` : f.degree;
}

export type Tip = {
  title: string;
  summary: string;
  use: string;
  example: string;
};

export const TIPS: Record<number, Tip> = {
  0: {
    title: "Position fondamentale",
    summary: "La tonique est à la basse. Son la plus stable.",
    use: "Idéale pour conclure un morceau, marquer une cadence, ancrer une mélodie. C'est le voicing par défaut de l'accord.",
    example: "Pop, comptines, fin de phrase musicale.",
  },
  1: {
    title: "1er renversement",
    summary: "La tierce est à la basse.",
    use: "Adoucit l'accord, rend la basse plus mélodique. Très utilisé pour la conduite des voix entre deux accords.",
    example: "Bach, ballades pop, transitions douces.",
  },
  2: {
    title: "2e renversement",
    summary: "La quinte est à la basse.",
    use: "Sonorité suspendue, instable. Souvent utilisé en transition (cadence 6/4) avant un accord de dominante.",
    example: "Hymnes, conclusions classiques, montées harmoniques.",
  },
  3: {
    title: "3e renversement",
    summary: "La septième est à la basse.",
    use: "Tension marquée qui appelle à la résolution. Caractéristique des accords de septième.",
    example: "Jazz, harmonies enrichies.",
  },
};

export const PEDAGOGY_PANEL_WIDTH = 420;
